import { useViewerStore } from "../store/store";
import { ReCalcShade, SetDisplayShadePoints } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/RefreshShade";
import { SetModuleGraph } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/ModuleControl";
import { ChangeTreeDelete } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/ObstructionControl";
import {
    clearTreeCutScenarioGhostState,
    ghostTreeForTreeCutScenario,
} from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/TreeImpactEligibility";
import { showTreeLabelsForSnapshot } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/LabelControl";
import { reapplyAllGhostTreeAppearances } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/TreeGhostVisual";
import { HideObjectType } from "../@types/enums";
import treeCartService, { getTreeCutCostWithDefaults } from "./treeCartService";
import { shouldDeferBackgroundTreeCalculatorWork } from "../helpers/dashboardViewerWorkGate";
import type {
    AiModuleFillingScenario,
    RoofShadeWeights,
    ShadePointWeight,
    TreeMultiplierEntry,
} from "../@types/types";
import type { TreeGroup } from "../@types/types";
import type { Viewer } from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Viewer-Factory/Viewer";

/** Minimum tree impact (multiplier gain if removed) to consider for ghost scenarios */
const TREE_IMPACT_THRESHOLD = 0.001;
/** Minimum production gain % to consider a scenario "worth it" for including in multi-tree combos */
const MIN_GAIN_PCT = 0.1;
/** Minimum gain-per-$1000 to consider worth it when cost > 0 */
const MIN_GAIN_PER_1K = 0.05;

function snapshotShadeWeightsFromViewer(self: Viewer): RoofShadeWeights[] {
    const state = useViewerStore.getState();
    const roofInfoArr = state.roofInfoArr ?? [];
    const roofMeshArr = self.roofMeshArr ?? [];
    const savedLayout = state.savedRoofModulesLayout ?? [];
    const accessMetaArr = state.savedRoofWiseAccessMultiplierDataArray ?? [];
    const accessForRoofs = state.savedAccessMultipliersForSolarRoofs ?? [];

    return roofInfoArr.map((r) => {
        const roofMesh = roofMeshArr.find((m: any) => m.roofFaceId === r.roofFaceId);
        const shadeArr = roofMesh?.userData?.shadeArrForRoof as Array<{ position?: { x: number; y: number }; ptHashVal?: number }> | undefined;
        const shadePoints: ShadePointWeight[] = (shadeArr ?? []).map((s, idx) => ({
            index: idx,
            ptHashVal: s.ptHashVal ?? 0,
            x: s.position?.x ?? 0,
            y: s.position?.y ?? 0,
        }));
        // Count actual module meshes in the scene (authoritative) rather than
        // roofMesh.moduleArr which can drift out of sync after grey-panel removal.
        let moduleCount = roofMesh
            ? (roofMesh as any).children.filter((c: any) => !!c.module).length
            : 0;
        if (moduleCount === 0) {
            // Fallback: try placement data when no meshes are present yet
            const arrLen = (roofMesh?.moduleArr ?? []).length;
            if (arrLen > 0) {
                moduleCount = arrLen;
            } else {
                const roofLayout = savedLayout.find((l: { roofFaceId?: string }) => l.roofFaceId === r.roofFaceId);
                moduleCount = (roofLayout?.moduleArr ?? []).length;
            }
        }

        const rawActual = (r as { actual_production?: string | number }).actual_production;
        const actualProduction =
            typeof rawActual === "number" && Number.isFinite(rawActual)
                ? rawActual
                : typeof rawActual === "string"
                ? parseFloat(rawActual)
                : undefined;

        // Attach access multipliers for this roof when available
        const metaForRoof = accessMetaArr.find((m) => m.roofFaceId === r.roofFaceId);
        const accessMultipliersSource =
            accessForRoofs.find(
                (a) =>
                    typeof a.azimuth === "number" &&
                    typeof a.tilt === "number" &&
                    Math.abs(a.azimuth - (r.roofAzimuth ?? r.azimuth ?? 0)) < 1e-3 &&
                    Math.abs(a.tilt - (r.roofTilt ?? r.tilt ?? 0)) < 1e-3,
            ) || null;

        const accessMultipliers =
            accessMultipliersSource && Array.isArray(accessMultipliersSource.multipliers)
                ? {
                      azimuth: accessMultipliersSource.azimuth,
                      tilt: accessMultipliersSource.tilt,
                      multipliers: accessMultipliersSource.multipliers.map((m) => ({
                          access: m.access,
                          multiplier: m.multiplier,
                          monthlyMultipliers: (m.monthlyMultipliers ?? []).map((mm) => ({
                              month: mm.month,
                              multiplier: mm.multiplier,
                          })),
                      })),
                  }
                : undefined;

        return {
            roofFaceId: r.roofFaceId,
            roofNum: r.roofNum,
            solarAccess: r.solarAccess,
            roofMultiplier: r.roofMultiplier,
            moduleCount,
            actualProduction: actualProduction != null && Number.isFinite(actualProduction) ? actualProduction : undefined,
            shadePoints: shadePoints.length ? shadePoints : undefined,
            // metadata primarily for prompt / analysis; base types already allow ptHashAverage etc.
            ptHashAverage: (metaForRoof as any)?.ptHashAverage,
            accessMultipliers,
        } as any;
    });
}

function snapshotTreeMultipliersFromViewer(self: Viewer): TreeMultiplierEntry[] {
    const entries: TreeMultiplierEntry[] = [];
    self.treeGroup.children.forEach((group: any) => {
        const treeId = group.userData?.treeId;
        if (!treeId) return;
        const treeImpact = Number(group.treeImpact ?? 0);
        if (!Number.isFinite(treeImpact) || treeImpact === 0) return;
        entries.push({
            treeId,
            treeLabel: treeId,
            treeImpactMulti: treeImpact,
        });
    });
    return entries;
}

/** Top-down terrain-map capture for tree-cutting cards (full map + visible ghosted trees). */
function captureTreeScenarioSnapshot(self: Viewer): string | undefined {
    const state = useViewerStore.getState() as {
        layerVisibilityState?: { maps?: boolean; tree?: boolean };
        isHideAllGhostTrees?: boolean;
    };
    const wasMapLayerVisible = state.layerVisibilityState?.maps ?? true;
    const wasTreeLayerVisible = state.layerVisibilityState?.tree ?? true;
    const wasHideAllGhostTrees = state.isHideAllGhostTrees ?? false;
    const wasMapCustomVisible = self.mapCustom?.visible ?? false;
    const wasTreeLabelGroupVisible = self.treeLabelGroup?.visible ?? true;
    const wasTreeTagGroupVisible = self.treeTagGroup?.visible ?? false;

    try {
        if (typeof self.setVisibleGroup === 'function') {
            self.setVisibleGroup('maps', true);
            self.setVisibleGroup('tree', true);
        }
        if (self.mapCustom) self.mapCustom.visible = true;
        if (self.treeLabelGroup) self.treeLabelGroup.visible = false;
        self.hideObject?.(HideObjectType.GhostTree, false);
        reapplyAllGhostTreeAppearances(self);
        showTreeLabelsForSnapshot(self);

        const img = self.getTerrainMapImageOfSite?.();
        if (img && typeof img === 'string' && img.trim().length > 0) {
            return img;
        }
    } catch {
        // fall through to undefined
    } finally {
        try {
            self.clearTreeLabel?.();
        } catch {
            // ignore
        }
        if (typeof self.setVisibleGroup === 'function') {
            self.setVisibleGroup('maps', wasMapLayerVisible);
            self.setVisibleGroup('tree', wasTreeLayerVisible);
        }
        if (self.mapCustom) self.mapCustom.visible = wasMapCustomVisible;
        if (self.treeLabelGroup) self.treeLabelGroup.visible = wasTreeLabelGroupVisible;
        if (self.treeTagGroup) self.treeTagGroup.visible = wasTreeTagGroupVisible;
        self.hideObject?.(HideObjectType.GhostTree, wasHideAllGhostTrees);
        reapplyAllGhostTreeAppearances(self);
    }

    return undefined;
}

function buildScenario(
    self: Viewer,
    treeLabel: string | null,
    treeId: string | null,
    cutCost: number | null,
    treeIds?: string[] | null
): AiModuleFillingScenario {
    const state = useViewerStore.getState();
    const multiplier = state.multiplier ?? 0;
    const actualWattage = state.actualWattage ?? 0;

    const {
        setbackNotes = [],
        aHJ_Township,
        siteAddress,
        moduleList,
        activeModule,
        treeArr,
        savedRoofFaceIdArray,
        savedRoofEdgeTypeCount,
    } = state as any;

    // Compact JSON sketch of roofs, obstructions, setbacks, and pathways so the LLM can visualize layout shapes.
    // We keep this intentionally small: only key polygon vertices and high-level counts.
    const sketch: any = {};
    try {
        const roofs: any[] = [];
        const roofFaces = (self.roofs?.[0]?.faces ?? []) as any[];
        roofFaces.forEach((face: any) => {
            const roofFaceId = face.roofFaceId;
            const posArr = Array.isArray(face.posArr) ? face.posArr : [];
            const vertices =
                posArr.length > 0
                    ? posArr.slice(0, 16).map((p: any) => ({ x: Number(p.x?.toFixed?.(2) ?? p.x ?? 0), z: Number(p.z?.toFixed?.(2) ?? p.z ?? 0) }))
                    : [];
            roofs.push({
                roofFaceId,
                roofNum: face.roofNum,
                buildingName: face.buildingName,
                tiltDeg: typeof face.tilt === "number" ? face.tilt : undefined,
                azimuthDeg: typeof face.azimuth === "number" ? face.azimuth : undefined,
                vertices,
            });
        });

        const obstructions: any[] = [];
        self.obstGroup?.children?.forEach((group: any, idx: number) => {
            const mesh = group.children?.[0]?.children?.[0]?.children?.[0];
            const userData = mesh?.userData ?? group.userData ?? {};
            const posArr = Array.isArray(userData.posArr) ? userData.posArr : [];
            const vertices =
                posArr.length > 0
                    ? posArr.slice(0, 12).map((p: any) => ({ x: Number(p.x?.toFixed?.(2) ?? p.x ?? 0), z: Number(p.z?.toFixed?.(2) ?? p.z ?? 0) }))
                    : [];
            obstructions.push({
                id: userData.obstructionId ?? idx,
                roofFaceId: userData.roofFaceId,
                type: userData.type,
                height: userData.height,
                vertices,
            });
        });

        const pathways: any[] = [];
        const setbacks: any[] = [];
        // Many helpers in ModuleControl/PathwayControl already derive pathway/setback polygons and store them
        // on roof meshes or helper arrays. We attempt to read a compact representation from roofMeshArr.
        self.roofMeshArr?.forEach((roofMesh: any) => {
            const roofFaceId = roofMesh.roofFaceId;
            const pathArr = roofMesh.userData?.pathwayArr ?? [];
            const setArr = roofMesh.userData?.setbackArr ?? [];
            pathArr.forEach((path: any) => {
                const posArr = Array.isArray(path.posArr) ? path.posArr : [];
                const vertices =
                    posArr.length > 0
                        ? posArr.slice(0, 16).map((p: any) => ({
                              x: Number(p.x?.toFixed?.(2) ?? p.x ?? 0),
                              z: Number(p.z?.toFixed?.(2) ?? p.z ?? 0),
                          }))
                        : [];
                pathways.push({
                    roofFaceId,
                    pathwayId: path.pathwayId,
                    active: path.pathActive,
                    vertices,
                });
            });
            setArr.forEach((sb: any) => {
                const posArr = Array.isArray(sb.posArr) ? sb.posArr : [];
                const vertices =
                    posArr.length > 0
                        ? posArr.slice(0, 16).map((p: any) => ({
                              x: Number(p.x?.toFixed?.(2) ?? p.x ?? 0),
                              z: Number(p.z?.toFixed?.(2) ?? p.z ?? 0),
                          }))
                        : [];
                setbacks.push({
                    roofFaceId,
                    type: sb.type,
                    vertices,
                });
            });
        });

        sketch.roofs = roofs;
        sketch.obstructions = obstructions;
        sketch.pathways = pathways;
        sketch.setbacks = setbacks;
        if (Array.isArray(savedRoofFaceIdArray) && savedRoofFaceIdArray.length) {
            sketch.roofFaceIds = savedRoofFaceIdArray;
        }
        if (savedRoofEdgeTypeCount) {
            sketch.setbackEdgeCounts = savedRoofEdgeTypeCount;
        }
    } catch {
        // Sketch is best-effort only; ignore errors.
    }

    const ahjRulesSummary =
        (Array.isArray(setbackNotes) && setbackNotes.length > 0) || aHJ_Township
            ? [
                  aHJ_Township ? `AHJ Township: ${aHJ_Township}` : "",
                  Array.isArray(setbackNotes) && setbackNotes.length > 0 ? `Setback notes:\n- ${setbackNotes.join("\n- ")}` : "",
              ]
                  .filter(Boolean)
                  .join("\n")
            : undefined;

    const moduleTypeSummary = activeModule
        ? `Active module: ${activeModule.text || ""}${
              activeModule.power ? `, power ${activeModule.power} W` : ""
          }${
              typeof activeModule.w === "number" && typeof activeModule.h === "number"
                  ? `, dimensions: ${activeModule.w}mm × ${activeModule.h}mm`
                  : ""
          }${
              activeModule.manufacturer ? `, manufacturer: ${activeModule.manufacturer}` : ""
          }`
        : Array.isArray(moduleList) && moduleList.length
        ? `Available modules: ${moduleList
              .map(
                  (m: any) =>
                      `${m.text}${
                          m.power ? ` ${m.power} W` : ""
                      }${typeof m.w === "number" && typeof m.h === "number" ? ` (${m.w}mm × ${m.h}mm)` : ""}${
                          m.manufacturer ? `, ${m.manufacturer}` : ""
                      }`,
              )
              .join("; ")}`
        : undefined;

    const treeMetaSummary =
        Array.isArray(treeArr) && treeArr.length
            ? `Trees on site: ${treeArr.length} (heights/diameters sampled in the shade model; higher trees and larger crowns generally create more shade).`
            : undefined;

    const drawingSummaryBase = siteAddress
        ? `Site address: ${siteAddress}. Roof and obstruction geometry, setbacks, and pathways are encoded in the shade model; use roofFaceId / solarAccess / multipliers and accessMultipliers to reason about layout quality.`
        : `Roof and obstruction geometry, setbacks, and pathways are encoded in the shade model; use roofFaceId / solarAccess / multipliers and accessMultipliers to reason about layout quality.`;

    const drawingSummarySketch =
        sketch && Object.keys(sketch).length
            ? `\n\nCompact layout sketch (JSON, truncated vertices):\n${JSON.stringify(sketch)}`
            : "";

    const drawingSummary = `${drawingSummaryBase}${drawingSummarySketch}`;

    const snapshotUrl = captureTreeScenarioSnapshot(self);

    return {
        treeLabel,
        treeId,
        treeIds: treeIds ?? undefined,
        cutCost,
        shadeWeights: snapshotShadeWeightsFromViewer(self),
        treeMultipliers: snapshotTreeMultipliersFromViewer(self),
        systemMultiplier: multiplier,
        actualWattage,
        ahjRulesSummary,
        drawingSummary,
        moduleTypeSummary,
        treeMetaSummary,
        snapshotUrl,
    };
}

/**
 * For each tree with high impact (treeImpactMulti > threshold): ghost it,
 * recalc shade, run SetModuleGraph, get cut cost with default params, snapshot scenario, then unghost.
 * Saves base scenario (no tree cut) + one scenario per high-impact tree to store.
 */
export async function runGhostScenariosAndSave(): Promise<void> {
    if (shouldDeferBackgroundTreeCalculatorWork()) {
        return;
    }

    const sceneViewer = useViewerStore.getState().sceneViewer as Viewer | null;
    if (!sceneViewer?.treeGroup) {
        useViewerStore.setState({
            aiModuleFillingError: "Scene viewer or trees not available.",
            aiModuleFillingLoading: false,
        });
        return;
    }

    useViewerStore.setState({
        aiModuleFillingLoading: true,
        aiModuleFillingError: null,
        aiModuleFillingScenarios: [],
    });

    const self = sceneViewer;
    const treeGroup = self.treeGroup;

    try {
        // Ensure production numbers and tree impacts are current
        await SetModuleGraph(self);

        const baseScenario = buildScenario(self, null, null, null);
        const scenarios: AiModuleFillingScenario[] = [baseScenario];

        const treesWithImpact: TreeGroup[] = [];
        treeGroup.children.forEach((group: any) => {
            const impact = group.treeImpact ?? 0;
            if (impact >= TREE_IMPACT_THRESHOLD) treesWithImpact.push(group as TreeGroup);
        });
        // Cap single-tree scenarios for faster initial run (take top by impact; rest can be run on demand).
        const MAX_SINGLE_TREE_SCENARIOS = 10;
        const sortedByImpact = [...treesWithImpact].sort((a, b) => (b.treeImpact ?? 0) - (a.treeImpact ?? 0));
        const treesToRun = sortedByImpact.slice(0, MAX_SINGLE_TREE_SCENARIOS);

        const token = await treeCartService.getExcelServiceToken();
        if (typeof token !== "string") {
            useViewerStore.setState({
                aiModuleFillingError: "Failed to get Excel service token for tree cost.",
                aiModuleFillingLoading: false,
            });
            return;
        }

        for (const tree of treesToRun) {
            await new Promise((r) => setTimeout(r, 0));
            const treeId = tree.userData?.treeId;
            if (!treeId) continue;

            const savedDelStatus = tree.delStatus;

            // Ghost this tree; use default layout only (no maxfill)
            ChangeTreeDelete(tree, true);
            self.reCalcShade([treeId], true);
            await SetModuleGraph(self);

            const cutCost = await getTreeCutCostWithDefaults(token, tree);
            const scenario = buildScenario(self, treeId, treeId, cutCost ?? null);
            scenarios.push(scenario);

            // Unghost and restore for next iteration
            ChangeTreeDelete(tree, savedDelStatus ?? false);
            self.reCalcShade([treeId], false);
            await SetModuleGraph(self);
        }

        // Multi-tree: only combinations of trees that were worth it as single cuts (actual run, not approx)
        const baseWattage = baseScenario.actualWattage ?? 0;
        const worthItTreeIds: string[] = [];
        const gainPctByTreeId: Record<string, number> = {};
        for (let i = 1; i < scenarios.length; i++) {
            const s = scenarios[i];
            if (s.treeId == null) continue;
            const gainPct = baseWattage ? ((s.actualWattage ?? 0) - baseWattage) / baseWattage * 100 : 0;
            gainPctByTreeId[s.treeId] = gainPct;
            const costNum = s.cutCost != null ? Number(s.cutCost) : NaN;
            const worthIt = gainPct >= MIN_GAIN_PCT && (costNum === 0 || !Number.isFinite(costNum) || gainPct / (costNum / 1000) >= MIN_GAIN_PER_1K);
            if (worthIt) worthItTreeIds.push(s.treeId);
        }
        const cutCostByTreeId: Record<string, number> = {};
        scenarios.forEach((s) => { if (s.treeId) cutCostByTreeId[s.treeId] = s.cutCost != null ? Number(s.cutCost) : 0; });

        /**
         * Build multi-tree combinations of size 2..maxK from the worth-it trees.
         * We limit:
         * - How many trees we consider (top N by individual gain).
         * - How many combinations we actually simulate (top MAX_MULTI_COMBOS by approximate combined gain).
         */
        const MAX_TREES_FOR_COMBINATIONS = 5;
        const MAX_MULTI_COMBOS = 10;
        const MAX_K = 3;

        const sortedTrees = [...worthItTreeIds].sort((a, b) => (gainPctByTreeId[b] ?? 0) - (gainPctByTreeId[a] ?? 0));
        const candidateTrees = sortedTrees.slice(0, MAX_TREES_FOR_COMBINATIONS);

        type Combo = { ids: string[]; approxGain: number };
        const combos: Combo[] = [];

        const buildCombos = (startIndex: number, current: string[], targetSize: number) => {
            if (current.length === targetSize) {
                const approxGain = current.reduce((sum, id) => sum + (gainPctByTreeId[id] ?? 0), 0);
                combos.push({ ids: [...current], approxGain });
                return;
            }
            // Simple pruning: if even with best remaining gains we can't beat the current smallest in top-N,
            // we still let it run because N is small and this is already bounded by MAX_TREES_FOR_COMBINATIONS.
            for (let i = startIndex; i < candidateTrees.length; i++) {
                current.push(candidateTrees[i]);
                buildCombos(i + 1, current, targetSize);
                current.pop();
            }
        };

        for (let k = 2; k <= MAX_K; k++) {
            if (candidateTrees.length < k) break;
            buildCombos(0, [], k);
            // Soft guard: if combos are exploding, stop at this k.
            if (combos.length > MAX_MULTI_COMBOS * 4) break;
        }

        combos.sort((a, b) => b.approxGain - a.approxGain);
        const combosToRun = combos.slice(0, MAX_MULTI_COMBOS);

        const setModuleGraphWithTimeout = (ms: number) =>
            Promise.race([SetModuleGraph(self), new Promise<never>((_, rej) => setTimeout(() => rej(new Error("SetModuleGraph timeout")), ms))]);

        for (const combo of combosToRun) {
            await new Promise((r) => setTimeout(r, 0));
            const ids = combo.ids;
            const trees: TreeGroup[] = ids
                .map((id) => treeGroup.children.find((g: any) => g.userData?.treeId === id) as TreeGroup | undefined)
                .filter(Boolean) as TreeGroup[];
            if (!trees.length || trees.length < 2) continue;

            try {
                restoreAllTrees(self);
                // Ensure ghost trees are visible while we evaluate multi-tree cuts (best-effort, typed as any to avoid coupling to enums here).
                try {
                    (self as any).hideObject?.('GhostTree', false);
                } catch {
                    // ignore
                }
                trees.forEach((t) => ChangeTreeDelete(t, true));
                self.reCalcShade(ids, true);
                await setModuleGraphWithTimeout(60000);
                await new Promise((resolve) => setTimeout(resolve, 100));

                const totalCutCost = ids.reduce((sum, id) => sum + (cutCostByTreeId[id] ?? 0), 0);
                const multiScenario = buildScenario(self, ids.join(', '), null, totalCutCost, ids);
                scenarios.push(multiScenario);
            } finally {
                trees.forEach((t) => ChangeTreeDelete(t, false));
                self.reCalcShade(ids, false);
                await SetModuleGraph(self);
            }
        }

        // Restore full state
        await SetModuleGraph(self);

        console.log('scenarios', scenarios);
        useViewerStore.setState({
            aiModuleFillingScenarios: scenarios,
            aiModuleFillingLoading: false,
            aiModuleFillingError: null,
        });
    } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        try {
            restoreAllTrees(self);
            await SetModuleGraph(self);
        } catch {
            // ignore restore errors
        }
        console.log('message', message);
        useViewerStore.setState({
            aiModuleFillingError: message,
            aiModuleFillingLoading: false,
            aiModuleFillingScenarios: [],
        });
    }
}

/** Build the input array for the AI prompt: base + per-tree scenarios with Tree Label, ShadeWeights, TreeMultipliers */
export function buildAiModuleFillingInput(): AiModuleFillingScenario[] {
    return useViewerStore.getState().aiModuleFillingScenarios ?? [];
}

/** Unghost all trees and recalc shade so we start from base state. */
export function restoreAllTrees(self: Viewer): void {
    const treeIds: string[] = [];
    self.treeGroup.children.forEach((group: any) => {
        const id = group.userData?.treeId;
        if (id) {
            treeIds.push(id);
            clearTreeCutScenarioGhostState(group);
            ChangeTreeDelete(group as TreeGroup, false);
        }
    });
    if (treeIds.length) self.reCalcShade(treeIds, false);
}

/**
 * Apply a scenario: restore all trees, then ghost the tree(s) for this scenario. Uses default layout only.
 * @param viewerOverride When the dashboard holds the viewer via props before `sceneViewer` is synced into the store, pass it here so ghosting and shade run on the live instance.
 */
export async function applyScenarioLayout(scenarioIndex: number, viewerOverride?: Viewer | null): Promise<void> {
    const sceneViewer = (viewerOverride ?? useViewerStore.getState().sceneViewer) as Viewer | null;
    const scenarios = useViewerStore.getState().aiModuleFillingScenarios ?? [];
    if (!sceneViewer || scenarioIndex < 0 || scenarioIndex >= scenarios.length) return;

    const self = sceneViewer;
    const scenario = scenarios[scenarioIndex];

    restoreAllTrees(self);
    const idsToGhost = scenario.treeIds?.length ? scenario.treeIds : (scenario.treeId ? [scenario.treeId] : []);
    if (idsToGhost.length) {
        for (const treeId of idsToGhost) {
            const tree = self.treeGroup.children.find((g: any) => g.userData?.treeId === treeId) as TreeGroup | undefined;
            if (tree) ghostTreeForTreeCutScenario(tree);
        }
        self.reCalcShade(idsToGhost, true);
    }
    await SetModuleGraph(self);
    SetDisplayShadePoints(self, null);
}

/**
 * Apply multi-tree cut: ghost the given trees and refresh production (no layout change).
 */
export async function applyMultiTreeCutScenario(treeIds: string[]): Promise<void> {
    const sceneViewer = useViewerStore.getState().sceneViewer as Viewer | null;
    if (!sceneViewer || !treeIds.length) return;

    const self = sceneViewer;
    restoreAllTrees(self);

    for (const treeId of treeIds) {
        const tree = self.treeGroup.children.find((g: any) => g.userData?.treeId === treeId) as TreeGroup | undefined;
        if (tree) ghostTreeForTreeCutScenario(tree);
    }
    if (treeIds.length) self.reCalcShade(treeIds, true);
    await SetModuleGraph(self);
}

/**
 * Apply only the tree-cutting part of a scenario: ghost the tree(s) and refresh production (no layout change).
 * Supports both single-tree (treeId) and multi-tree (treeIds) scenarios. Updates shade and irradiance display.
 */
export async function applyTreeCutScenario(scenarioIndex: number): Promise<void> {
    const sceneViewer = useViewerStore.getState().sceneViewer as Viewer | null;
    const scenarios = useViewerStore.getState().aiModuleFillingScenarios ?? [];
    if (!sceneViewer || scenarioIndex < 0 || scenarioIndex >= scenarios.length) return;

    const scenario = scenarios[scenarioIndex];
    const idsToGhost = scenario.treeIds?.length ? scenario.treeIds : (scenario.treeId != null ? [scenario.treeId] : []);
    if (idsToGhost.length === 0) return;

    const self = sceneViewer;
    restoreAllTrees(self);

    for (const treeId of idsToGhost) {
        const tree = self.treeGroup.children.find((g: any) => g.userData?.treeId === treeId) as TreeGroup | undefined;
        if (tree) ghostTreeForTreeCutScenario(tree);
    }
    self.reCalcShade(idsToGhost, true);
    await SetModuleGraph(self);
    SetDisplayShadePoints(self, null);
}
